import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import CustomerDetailBackfillPanel from '@/components/admin/CustomerDetailBackfillPanel';
import { format } from 'date-fns';
import {
  Loader2, ShieldOff, ArrowLeft, Wrench, Rocket, CheckCircle2, XCircle,
  Play, RefreshCw, ChevronDown, ChevronRight, Clock, User, ListChecks,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Checklist definitions
// ---------------------------------------------------------------------------

// Pre-launch items. `runCheck` (optional) is an async verifier that returns
// { ok: boolean, summary: string }. Items without runCheck are manual-only.
const PRELAUNCH_ITEMS = [
  {
    key: 'pl_live_payments',
    label: 'Run a live payment through each path',
    description: 'Test a shop order (Square), a standalone booking (Square), a gift card purchase, and a £5 competition entry (Stripe). Confirm the record, invoice PDF and confirmation email all land.',
    group: 'Blockers',
  },
  {
    key: 'pl_webhooks_registered',
    label: 'Register webhooks against the published domain',
    description: 'Square + Stripe webhook endpoints in the provider dashboards point at https://motion-reality-website.base44.app/functions/... and the Stripe webhook secret matches.',
    group: 'Blockers',
    runCheck: async () => {
      const results = await Promise.allSettled([
        fetch('/functions/squareWebhook', { method: 'GET' }),
        fetch('/functions/stripeCompetitionWebhook', { method: 'GET' }),
      ]);
      const reachable = results.filter(r => r.status === 'fulfilled' && r.value.status < 500).length;
      return {
        ok: reachable === 2,
        summary: reachable === 2
          ? 'Both webhook endpoints are reachable on the live domain.'
          : `${reachable}/2 webhook endpoints reachable — verify the registrations in the Square & Stripe dashboards.`,
      };
    },
  },
  {
    key: 'pl_custom_domain',
    label: 'Connect the custom domain',
    description: 'Connect motionreality.co.uk (or equivalent). A paid plan + connected custom domain is also required for order/invoice emails to non-registered recipients.',
    group: 'Blockers',
  },
  {
    key: 'pl_invoice_numbering',
    label: 'Set invoice numbering continuity',
    description: 'InvoiceSettings.next_invoice_number is set to your last Sage invoice + 1 so the first real sale continues the global sequence (no INV- prefix, no leading zeros).',
    group: 'Blockers',
    runCheck: async () => {
      const res = await base44.entities.InvoiceSettings.list();
      const s = Array.isArray(res) ? res[0] : res;
      const next = s?.next_invoice_number;
      return {
        ok: !!next && next > 0,
        summary: next ? `Next invoice number is ${next}.` : 'No InvoiceSettings record found — set next_invoice_number before launch.',
      };
    },
  },
  {
    key: 'pl_seo_gsc_sitemap',
    label: 'Submit sitemap to Google Search Console',
    description: 'Submit the sitemap to GSC (connector authorised), run IndexNow on key pages, and check index.html has correct Open Graph / title tags.',
    group: 'Should-do',
    runCheck: async () => {
      const res = await base44.functions.invoke('sitemap', {});
      return {
        ok: !res?.data?.error,
        summary: res?.data?.url ? `Sitemap regenerated: ${res.data.url}` : 'Sitemap generation completed.',
      };
    },
  },
  {
    key: 'pl_catalog_content',
    label: 'Finalise catalog & content',
    description: 'Square product re-import is current, categories active and ordered, featured products flagged, and homepage/Blog/About copy is final.',
    group: 'Should-do',
  },
  {
    key: 'pl_royal_mail_oba',
    label: 'Migrate Royal Mail Click & Drop to an OBA account',
    description: 'Move the Click & Drop account from OLP (pay-as-you-go) to OBA so API label generation unlocks. The dispatch panel currently runs on a fallback service list with no label retrieval until OBA is active — confirm labels generate and print end-to-end from an order before launch.',
    group: 'Should-do',
  },
  {
    key: 'pl_legal_compliance',
    label: 'Legal & competition terms review',
    description: 'Privacy, Terms, Refund and Delivery pages present. Quick legal sense-check on the paid £5 sim-rig draw terms (prize-competition / licensing territory).',
    group: 'Should-do',
  },
  {
    key: 'pl_admin_access',
    label: 'Invite real admin users & test B2B invite',
    description: 'Invite admins via adminInviteAdmin; confirm each can log in. Verify the B2B portal invite flow end-to-end for at least one trade customer.',
    group: 'Should-do',
  },
  {
    key: 'pl_email_deliverability',
    label: 'Test email deliverability',
    description: 'Send a test order-confirmation and a competition winner notification to a real address to confirm Resend / custom-domain sending works.',
    group: 'Should-do',
  },
  {
    key: 'pl_mobile_pass',
    label: 'Mobile tap-through of booking & checkout',
    description: 'Run the full booking wizard and checkout on a real phone at ~380px to confirm nothing is broken on mobile.',
    group: 'Nice-to-have',
  },
  {
    key: 'pl_social_ebay_tokens',
    label: 'Refresh social & eBay tokens',
    description: 'Confirm Instagram/Facebook publish tokens are fresh (they expire) and the eBay refresh token is valid if listing on launch day.',
    group: 'Nice-to-have',
    runCheck: async () => {
      const res = await base44.functions.invoke('ebaySellerOverview', {});
      const d = res?.data || {};
      return {
        ok: !d.error,
        summary: d.connected ? `eBay connected as ${d.seller_username || 'seller'}.` : (d.error || 'eBay connection could not be verified — re-authorise from the eBay admin.'),
      };
    },
  },
  {
    key: 'pl_backups',
    label: 'Run a backup before flipping live',
    description: 'Synology backup guide / NAS bundle exists — make sure a backup actually runs before launch day.',
    group: 'Nice-to-have',
  },
];

// Monthly maintenance items. `functionName` + `payload` are invoked on Run now.
const MONTHLY_ITEMS = [
  {
    key: 'm_loyalty_activity_backfill',
    label: 'Backfill loyalty activity timestamps',
    description: 'Sets last_activity_at on every ConsumerAccount from the most recent qualifying Order or Booking created_date. Run once when the 12-month inactivity expiry launches so each customer\'s clock starts from their last purchase.',
    functionName: 'backfillLoyaltyActivity',
    payload: {},
  },
  {
    key: 'm_booking_orders_backfill',
    label: 'Backfill booking sales orders',
    description: 'Creates a VAT-invoiceable Sales order for every past confirmed/completed booking that doesn\'t already have one, with an invoice PDF generated for audit/Sage (no customer email). Run once to bring historical bookings into the Sales list.',
    functionName: 'backfillBookingOrders',
    payload: {},
  },
  {
    key: 'm_square_sync',
    label: 'Re-sync Square product catalog',
    description: 'Pull the latest Square catalog into Base44 so prices, stock and new items stay current.',
    functionName: 'reimportSquareProducts',
    payload: {},
  },
  {
    key: 'm_ebay_listings',
    label: 'Sync eBay listings',
    description: 'Push current product inventory live to eBay and reconcile any ended listings.',
    functionName: 'ebaySyncListings',
    payload: {},
  },
  {
    key: 'm_ebay_orders',
    label: 'Pull eBay orders',
    description: 'Import new eBay orders so they appear in the orders queue and Sage export.',
    functionName: 'ebaySyncOrders',
    payload: {},
  },
  {
    key: 'm_social_engagement',
    label: 'Refresh social engagement stats',
    description: 'Refresh like / comment counts on published Instagram & Facebook posts.',
    functionName: 'refreshSocialEngagement',
    payload: {},
  },
  {
    key: 'm_indexnow',
    label: 'Resubmit key pages to IndexNow',
    description: 'Ping IndexNow for the homepage, shop, bookings and blog so search engines re-crawl recent changes.',
    functionName: 'notifyIndexNow',
    payload: {},
  },
  {
    key: 'm_sitemap',
    label: 'Regenerate sitemap',
    description: 'Rebuild the XML sitemap and resubmit to Google Search Console.',
    functionName: 'sitemap',
    payload: {},
  },
  {
    key: 'm_sage_export',
    label: 'Run Sage export queue',
    description: 'Export reconciled orders / B2B invoices / paid competition entries to the Sage Audit Trail CSV.',
    functionName: 'exportSalesToSage',
    payload: {},
  },
  {
    key: 'm_competition_draw',
    label: 'Run monthly competition draw',
    description: 'Draw the monthly free-experience winner if the month is closed and a winner is due.',
    functionName: 'drawCompetitionWinner',
    payload: {},
  },
  {
    key: 'm_consumer_catchup',
    label: 'Consumer account catch-up sweep',
    description: 'Link any unlinked consumer accounts and re-target stale sign-up invites.',
    functionName: 'consumerInviteStaleRetarget',
    payload: {},
  },
  {
    key: 'm_low_stock_alert',
    label: 'Low stock alert sweep',
    description: 'Trigger the low-stock notification check for any products below threshold.',
    functionName: 'lowStockAlert',
    payload: {},
  },
  {
    key: 'm_tripadvisor_reviews',
    label: 'Pull TripAdvisor reviews',
    description: 'Fetch the latest TripAdvisor reviews via the Content API so they appear in the reviews carousel and rating summary.',
    functionName: 'tripadvisorReviewsPull',
    payload: {},
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmtDateTime = (iso) => {
  try { return format(new Date(iso), 'dd/MM/yyyy HH:mm'); } catch { return iso; }
};

const GROUP_ORDER = ['Blockers', 'Should-do', 'Nice-to-have'];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AdminMaintenance() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState({});   // key -> boolean
  const [showHistory, setShowHistory] = useState(false);

  const logsQuery = useQuery({
    queryKey: ['maintenance-logs'],
    queryFn: async () => {
      const res = await base44.entities.MaintenanceLog.list('-created_date', 200);
      return Array.isArray(res) ? res : (res?.data || []);
    },
    enabled: currentUser?.role === 'admin',
  });

  const logs = logsQuery.data || [];

  // Latest entry per (section, item_key)
  const latestByItem = {};
  for (const l of logs) {
    const k = `${l.section}|${l.item_key}`;
    if (!latestByItem[k] || new Date(l.created_date) > new Date(latestByItem[k].created_date)) {
      latestByItem[k] = l;
    }
  }

  const prelaunchDoneKeys = new Set(
    Object.values(latestByItem)
      .filter(l => l.section === 'prelaunch' && l.status === 'done')
      .map(l => l.item_key)
  );

  const monthlyHistory = logs
    .filter(l => l.section === 'monthly')
    .sort((a, b) => new Date(b.created_date) - new Date(a.created_date));

  const writeLog = async (entry) => {
    await base44.entities.MaintenanceLog.create({
      section: entry.section,
      item_key: entry.item_key,
      status: entry.status,
      summary: entry.summary || '',
      run_by: currentUser?.email || 'admin',
    });
    await queryClient.invalidateQueries({ queryKey: ['maintenance-logs'] });
  };

  const togglePrelaunch = async (item) => {
    const isDone = prelaunchDoneKeys.has(item.key);
    setBusy((b) => ({ ...b, [item.key]: true }));
    try {
      if (isDone) {
        // Clear the done confirmation by logging a non-done marker is not clean;
        // instead delete the latest done row for this item.
        const existing = latestByItem[`prelaunch|${item.key}`];
        if (existing) await base44.entities.MaintenanceLog.delete(existing.id);
        await queryClient.invalidateQueries({ queryKey: ['maintenance-logs'] });
        toast({ description: `Unchecked "${item.label}".` });
      } else {
        await writeLog({
          section: 'prelaunch',
          item_key: item.key,
          status: 'done',
          summary: 'Marked as complete.',
        });
        toast({ description: `Confirmed "${item.label}".` });
      }
    } catch (err) {
      toast({ variant: 'destructive', description: 'Could not update checklist. Please try again.' });
    }
    setBusy((b) => ({ ...b, [item.key]: false }));
  };

  const runPrelaunchCheck = async (item) => {
    if (!item.runCheck) return;
    setBusy((b) => ({ ...b, [`${item.key}_check`]: true }));
    try {
      const result = await item.runCheck();
      await writeLog({
        section: 'prelaunch',
        item_key: item.key,
        status: result.ok ? 'run_success' : 'run_failed',
        summary: result.summary,
      });
      toast({
        variant: result.ok ? 'default' : 'destructive',
        description: result.summary,
      });
    } catch (err) {
      const msg = err?.message || 'Run check failed.';
      await writeLog({ section: 'prelaunch', item_key: item.key, status: 'run_failed', summary: msg });
      toast({ variant: 'destructive', description: msg });
    }
    setBusy((b) => ({ ...b, [`${item.key}_check`]: false }));
  };

  const runMonthly = async (item) => {
    setBusy((b) => ({ ...b, [item.key]: true }));
    try {
      const res = await base44.functions.invoke(item.functionName, item.payload || {});
      const d = res?.data || res;
      const ok = !d?.error;
      const summary = d?.summary || d?.message || (ok ? 'Completed successfully.' : (d?.error || 'Completed with errors.'));
      // The TripAdvisor pull self-logs a MaintenanceLog (so the scheduled daily run
      // is recorded too); skip writing a second log here to avoid duplicates.
      if (item.functionName !== 'tripadvisorReviewsPull') {
        await writeLog({
          section: 'monthly',
          item_key: item.key,
          status: ok ? 'run_success' : 'run_failed',
          summary,
        });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['maintenance-logs'] });
      }
      toast({
        variant: ok ? 'default' : 'destructive',
        description: `${item.label}: ${summary}`,
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Run failed.';
      await writeLog({ section: 'monthly', item_key: item.key, status: 'run_failed', summary: msg });
      toast({ variant: 'destructive', description: `${item.label}: ${msg}` });
    }
    setBusy((b) => ({ ...b, [item.key]: false }));
  };

  // -----------------------------------------------------------------------
  // Access gate
  // -----------------------------------------------------------------------
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          <p className="text-white/20 text-xs mt-2">You must be an admin to view this page.</p>
        </div>
      </div>
    );
  }

  const prelaunchProgress = Math.round((prelaunchDoneKeys.size / PRELAUNCH_ITEMS.length) * 100);
  const lastRunByKey = {};
  for (const l of logs.filter(x => x.section === 'monthly' && x.status !== 'done')) {
    if (!lastRunByKey[l.item_key] || new Date(l.created_date) > new Date(lastRunByKey[l.item_key].created_date)) {
      lastRunByKey[l.item_key] = l;
    }
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">SYSTEM &amp; INTEGRATIONS</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase flex items-center gap-3">
            <Wrench className="w-7 h-7 text-primary" /> MAINTENANCE
          </h1>
          <p className="text-white/40 text-sm mt-3 max-w-2xl">
            Pre-launch readiness and recurring monthly upkeep. Progress is shared across all admins and every automation run is logged.
          </p>
          <button
            onClick={() => logsQuery.refetch()}
            className="mt-5 inline-flex items-center gap-2 border border-white/10 hover:border-primary/50 text-white/50 hover:text-white text-xs font-bold tracking-wider px-4 py-2.5 transition-all"
          >
            <RefreshCw className="w-4 h-4" /> REFRESH
          </button>
        </div>
      </section>

      <section className="px-6 lg:px-10 pt-10 max-w-6xl mx-auto pb-20 space-y-12">
        {/* ---------------- Pre-launch checklist ---------------- */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-white font-black text-xl uppercase flex items-center gap-2.5">
              <Rocket className="w-5 h-5 text-primary" /> Pre-Launch Checklist
            </h2>
            <div className="flex items-center gap-3">
              <div className="w-40 h-1.5 bg-white/5 overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${prelaunchProgress}%` }} />
              </div>
              <span className="text-white/40 text-xs font-bold tracking-wider w-10 text-right">{prelaunchProgress}%</span>
            </div>
          </div>

          {GROUP_ORDER.map((group) => {
            const items = PRELAUNCH_ITEMS.filter((i) => i.group === group);
            if (!items.length) return null;
            return (
              <div key={group} className="mb-6">
                <p className="text-white/30 text-[10px] font-bold tracking-widest mb-3">{group.toUpperCase()}</p>
                <div className="space-y-2.5">
                  {items.map((item) => {
                    const done = prelaunchDoneKeys.has(item.key);
                    const isBusy = busy[item.key] || busy[`${item.key}_check`];
                    const lastCheck = latestByItem[`prelaunch|${item.key}`];
                    return (
                      <div
                        key={item.key}
                        className={`bg-[#111115] border p-4 transition-colors ${done ? 'border-primary/30' : 'border-white/5'}`}
                      >
                        <div className="flex items-start gap-3">
                          <button
                            onClick={() => togglePrelaunch(item)}
                            disabled={isBusy}
                            className="mt-0.5 flex-shrink-0 w-5 h-5 border flex items-center justify-center transition-colors disabled:opacity-50"
                            style={{
                              borderColor: done ? 'var(--primary)' : 'rgba(255,255,255,0.15)',
                              background: done ? 'var(--primary)' : 'transparent',
                            }}
                          >
                            {isBusy ? (
                              <Loader2 className="w-3 h-3 animate-spin text-white" />
                            ) : done ? (
                              <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                            ) : null}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-bold ${done ? 'text-white' : 'text-white/80'}`}>{item.label}</p>
                            <p className="text-white/40 text-xs mt-1 leading-relaxed">{item.description}</p>
                            {lastCheck && lastCheck.status !== 'done' && (
                              <p className={`text-[11px] mt-2 flex items-center gap-1.5 ${lastCheck.status === 'run_success' ? 'text-emerald-400/80' : 'text-red-400/80'}`}>
                                {lastCheck.status === 'run_success' ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                                {lastCheck.summary}
                                <span className="text-white/20">· {fmtDateTime(lastCheck.created_date)}</span>
                              </p>
                            )}
                          </div>
                          {item.runCheck && (
                            <button
                              onClick={() => runPrelaunchCheck(item)}
                              disabled={isBusy}
                              className="flex-shrink-0 text-[10px] font-bold tracking-wider border border-white/10 hover:border-primary/50 text-white/60 hover:text-white px-3 py-2 transition-all disabled:opacity-40 flex items-center gap-1.5"
                            >
                              {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} RUN CHECK
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* ---------------- Customer detail tidy-up ---------------- */}
        <CustomerDetailBackfillPanel />

        {/* ---------------- Monthly maintenance ---------------- */}
        <div>
          <h2 className="text-white font-black text-xl uppercase flex items-center gap-2.5 mb-6">
            <ListChecks className="w-5 h-5 text-primary" /> Monthly Maintenance
          </h2>
          <div className="space-y-2.5">
            {MONTHLY_ITEMS.map((item) => {
              const last = lastRunByKey[item.key];
              const isBusy = busy[item.key];
              return (
                <div key={item.key} className="bg-[#111115] border border-white/5 p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-bold">{item.label}</p>
                      <p className="text-white/40 text-xs mt-1 leading-relaxed">{item.description}</p>
                      {last && (
                        <p className={`text-[11px] mt-2 flex items-center gap-1.5 ${last.status === 'run_success' ? 'text-emerald-400/80' : 'text-red-400/80'}`}>
                          {last.status === 'run_success' ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                          <span>Last run {fmtDateTime(last.created_date)}</span>
                          <span className="text-white/20">· by {last.run_by || 'admin'}</span>
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => runMonthly(item)}
                      disabled={isBusy}
                      className="flex-shrink-0 bg-primary hover:bg-primary/90 text-white font-bold text-[10px] tracking-wider px-4 py-2.5 transition-colors disabled:opacity-40 flex items-center gap-1.5"
                    >
                      {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} RUN NOW
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* History */}
          <div className="mt-8">
            <button
              onClick={() => setShowHistory((s) => !s)}
              className="inline-flex items-center gap-2 text-white/50 hover:text-white text-xs font-bold tracking-wider transition-colors"
            >
              {showHistory ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              RUN HISTORY ({monthlyHistory.length})
            </button>
            {showHistory && (
              <div className="mt-4 bg-[#111115] border border-white/5 max-h-96 overflow-y-auto">
                {monthlyHistory.length === 0 ? (
                  <p className="text-white/30 text-xs py-8 text-center">No maintenance runs logged yet.</p>
                ) : (
                  <table className="w-full text-left text-xs">
                    <thead className="sticky top-0 bg-[#111115] border-b border-white/5">
                      <tr className="text-white/30 font-bold tracking-wider">
                        <th className="px-4 py-2.5 w-8"></th>
                        <th className="px-4 py-2.5">TASK</th>
                        <th className="px-4 py-2.5">RESULT</th>
                        <th className="px-4 py-2.5">BY</th>
                        <th className="px-4 py-2.5 text-right">WHEN</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyHistory.map((l) => {
                        const item = MONTHLY_ITEMS.find((m) => m.key === l.item_key);
                        return (
                          <tr key={l.id} className="border-b border-white/5 last:border-0">
                            <td className="px-4 py-2.5">
                              {l.status === 'run_success' ? (
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                              ) : (
                                <XCircle className="w-3.5 h-3.5 text-red-400" />
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-white font-medium">{item?.label || l.item_key}</td>
                            <td className="px-4 py-2.5 text-white/50 max-w-xs truncate" title={l.summary}>{l.summary || '—'}</td>
                            <td className="px-4 py-2.5 text-white/40"><span className="flex items-center gap-1"><User className="w-3 h-3" />{l.run_by || 'admin'}</span></td>
                            <td className="px-4 py-2.5 text-white/40 text-right whitespace-nowrap"><span className="flex items-center justify-end gap-1"><Clock className="w-3 h-3" />{fmtDateTime(l.created_date)}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}